import type React from "react";
import { Box, Typography, IconButton, Tooltip, Paper } from "@mui/material";
import { Download } from "@mui/icons-material";
import DynamicThumbnailCard from "../../components/thumbnail/ThumbnailCard";
import MemorialGraphicCard from "../../components/share-message/ShareMessageThumbnailCard";
import type { IStreamDetails } from "../../../../types/streams";
import formatDate, { formatDateToLongString } from "../../../../utils/FormatDate";
import { formatTime } from "../../../../utils/FormatTime";
import sharemessagegraphicpattern from "../../components/share-message/pattern.png";
import muzukurulogo from "../../../../assets/muzukurublack.png";

interface MediaTabProps {
  stream: IStreamDetails;
  streamId?: number;
  deceasedName: string;
  refreshTrigger: number;
  downloadFn: (() => void) | null;
  onReadyDownload: (fn: () => void) => void;
}

const MediaTab: React.FC<MediaTabProps> = ({
  stream,
  streamId,
  deceasedName,
  refreshTrigger,
  downloadFn,
  onReadyDownload,
}) => {
  const isFuneral =
    stream.booking?.service?.toLowerCase() === "funeral" ||
    stream.booking?.service?.toLowerCase() === "funeralhome";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Stream Thumbnail
          </Typography>
          <Tooltip title="Download Thumbnail">
            <span>
              <IconButton onClick={() => downloadFn?.()} disabled={!downloadFn}>
                <Download />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic", mb: 1.5 }}
        >
          The thumbnail generated for this stream
        </Typography>
        <DynamicThumbnailCard
          streamId={streamId}
          streamData={stream}
          refreshTrigger={refreshTrigger}
          height={250}
          width={350}
          onReadyDownload={(fn) => onReadyDownload(fn)}
        />
      </Paper>

      {isFuneral && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1.5 }}>
            Memorial Graphic
          </Typography>
          <MemorialGraphicCard
            streamId={streamId}
            fullName={deceasedName}
            funeralStartTime={formatTime(stream.start)}
            birthDate={formatDate(stream.booking.deceased[0]?.date_of_birth)}
            deathDate={formatDate(stream.booking.deceased[0]?.date_of_death)}
            funeralDateText={formatDateToLongString(stream.date)}
            venueText={`${stream.address_one || ""}, ${stream.city || ""}`}
            imageUrl={stream.booking.deceased[0]?.person_image || ""}
            patternSvgUrl={sharemessagegraphicpattern}
            livestreamedBy={muzukurulogo}
          />
        </Paper>
      )}
    </Box>
  );
};

export default MediaTab;
